// Calendar Helper Functions
// Utility functions for calendar operations and data management

import { 
  holidayCalendar, 
  academicCalendar, 
  schoolEvents,
  eventCategories,
  eventTypes,
  getEventsByDate as getEventsByDateBase
} from './calendarData';

import { 
  cycleDays, 
  termDates, 
  examPeriods 
} from './calendarConfig';

// Event Management Functions
export const createEvent = (eventData) => {
  const newEvent = {
    id: `custom_${Date.now()}`,
    date: eventData.date,
    title: eventData.title,
    titleEn: eventData.titleEn || eventData.title,
    type: eventData.type || 'activity',
    category: eventData.category || 'academic',
    description: eventData.description || '',
    targetAudience: eventData.targetAudience || ['全校'],
    duration: eventData.duration || 1,
    location: eventData.location || '',
    createdAt: new Date().toISOString(),
    createdBy: eventData.createdBy || 'system'
  };
  
  return newEvent;
};

export const updateEvent = (eventId, updateData) => {
  // In a real implementation, this would update the database
  return {
    ...updateData,
    id: eventId,
    updatedAt: new Date().toISOString()
  };
};

export const deleteEvent = (eventId) => {
  // In a real implementation, this would delete from database
  return { success: true, deletedId: eventId };
};

export const addEventToCalendar = (calendarType, event) => {
  // Add event to specific calendar (academic, school, holiday)
  const calendars = {
    academic: academicCalendar,
    school: schoolEvents,
    holiday: holidayCalendar
  };
  
  if (calendars[calendarType]) {
    calendars[calendarType].push(event);
    return { success: true, event };
  }
  
  return { success: false, error: 'Invalid calendar type' };
};

export const removeEventFromCalendar = (calendarType, eventId) => {
  // Remove event from specific calendar
  const calendars = {
    academic: academicCalendar,
    school: schoolEvents,
    holiday: holidayCalendar
  };
  
  if (calendars[calendarType]) {
    const index = calendars[calendarType].findIndex(event => event.id === eventId);
    if (index !== -1) {
      const removedEvent = calendars[calendarType].splice(index, 1)[0];
      return { success: true, removedEvent };
    }
  }
  
  return { success: false, error: 'Event not found' };
};

// Calendar Calculation Functions
export const getCycleDayForDate = (date) => {
  const targetDate = new Date(date);
  const startDate = new Date(cycleDays.startDate);
  
  // Check if date is a skip date (holiday)
  if (cycleDays.skipDates.includes(date)) {
    return null;
  }
  
  // Calculate days difference, excluding weekends and skip dates
  let dayCount = 0;
  const currentDate = new Date(startDate);
  
  while (currentDate < targetDate) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dateStr = currentDate.toISOString().split('T')[0];
    
    // Skip weekends and holidays
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6 && 
        !cycleDays.skipDates.includes(dateStr)) {
      dayCount++;
    }
  }
  
  const cycleDay = (dayCount % cycleDays.cycleLength) + 1;
  return cycleDays.days.find(day => day.id === cycleDay);
};

export const getTermForDate = (date) => {
  const dateObj = new Date(date);
  
  if (dateObj >= new Date(termDates.term1.startDate) && 
      dateObj <= new Date(termDates.term1.endDate)) {
    return termDates.term1;
  }
  
  if (dateObj >= new Date(termDates.term2.startDate) && 
      dateObj <= new Date(termDates.term2.endDate)) {
    return termDates.term2;
  }
  
  if (dateObj >= new Date(termDates.summerBreak.startDate) && 
      dateObj <= new Date(termDates.summerBreak.endDate)) {
    return termDates.summerBreak;
  }
  
  return null;
};

export const isExamPeriod = (date) => {
  const allExams = [
    ...examPeriods.uniformTests,
    ...examPeriods.termExams,
    ...examPeriods.mockExams
  ];
  
  return allExams.some(exam => {
    const startDate = new Date(exam.startDate);
    const endDate = new Date(exam.endDate);
    const checkDate = new Date(date);
    
    return checkDate >= startDate && checkDate <= endDate;
  });
};

export const getExamForDate = (date) => {
  const allExams = [
    ...examPeriods.uniformTests,
    ...examPeriods.termExams,
    ...examPeriods.mockExams
  ];
  
  return allExams.find(exam => {
    const startDate = new Date(exam.startDate);
    const endDate = new Date(exam.endDate);
    const checkDate = new Date(date);
    
    return checkDate >= startDate && checkDate <= endDate;
  });
};

// Enhanced Event Retrieval Functions
export const getEventsForDateRange = (startDate, endDate) => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  return allEvents.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate >= start && eventDate <= end;
  });
};

export const getEventsByAudience = (audience) => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  return allEvents.filter(event => 
    event.targetAudience && event.targetAudience.includes(audience)
  );
};

export const getEventsByLocation = (location) => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  return allEvents.filter(event => 
    event.location && event.location.includes(location)
  );
};

export const searchEvents = (searchTerm) => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  const term = searchTerm.toLowerCase();
  
  return allEvents.filter(event => 
    event.title.toLowerCase().includes(term) ||
    (event.titleEn && event.titleEn.toLowerCase().includes(term)) ||
    (event.description && event.description.toLowerCase().includes(term))
  );
};

// Statistics and Analytics Functions
export const getEventStatistics = () => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  
  const stats = {
    total: allEvents.length,
    byCategory: {},
    byType: {},
    byMonth: {},
    upcoming: 0,
    thisMonth: 0
  };
  
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  
  allEvents.forEach(event => {
    // By category
    stats.byCategory[event.category] = (stats.byCategory[event.category] || 0) + 1;
    
    // By type
    stats.byType[event.type] = (stats.byType[event.type] || 0) + 1;
    
    // By month
    const eventMonth = event.date.substring(0, 7);
    stats.byMonth[eventMonth] = (stats.byMonth[eventMonth] || 0) + 1;
    
    // Upcoming events
    if (new Date(event.date) > now) {
      stats.upcoming++;
    }
    
    // This month events
    if (eventMonth === thisMonth) {
      stats.thisMonth++;
    }
  });
  
  return stats;
};

export const getPopularEvents = (limit = 10) => {
  // In a real implementation, this would be based on actual engagement data
  const allEvents = [...academicCalendar, ...schoolEvents];
  
  // Mock popularity based on target audience size and event type
  const eventsWithPopularity = allEvents.map(event => ({
    ...event,
    popularity: (event.targetAudience?.length || 1) * 
                (event.type === 'ceremony' ? 3 : 
                 event.type === 'competition' ? 2.5 : 
                 event.type === 'activity' ? 2 : 1)
  }));
  
  return eventsWithPopularity
    .sort((a, b) => b.popularity - a.popularity)
    .slice(0, limit);
};

export const getRecentEvents = (limit = 10) => {
  const allEvents = [...holidayCalendar, ...academicCalendar, ...schoolEvents];
  const now = new Date();
  
  const recentEvents = allEvents
    .filter(event => new Date(event.date) <= now)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, limit);
    
  return recentEvents;
};

// Calendar Generation Functions
export const generateAcademicYearCalendar = (year) => {
  const startDate = new Date(year, 8, 1); // September 1st
  const endDate = new Date(year + 1, 5, 30); // June 30th
  
  const calendar = [];
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const events = getEventsByDateBase(dateStr);
    const cycleDay = getCycleDayForDate(dateStr);
    const term = getTermForDate(dateStr);
    const exam = getExamForDate(dateStr);
    
    calendar.push({
      date: dateStr,
      dayOfWeek: currentDate.getDay(),
      events: events,
      cycleDay: cycleDay,
      term: term,
      isExamPeriod: !!exam,
      exam: exam,
      isHoliday: events.some(event => event.category === 'holiday'),
      isWeekend: currentDate.getDay() === 0 || currentDate.getDay() === 6
    });
    
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return calendar;
};

export const generateMonthCalendar = (year, month) => {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const calendar = [];
  
  // Add empty cells for days before the first day of the month
  const startDayOfWeek = firstDay.getDay();
  for (let i = 0; i < startDayOfWeek; i++) {
    calendar.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month - 1, day);
    const dateStr = date.toISOString().split('T')[0];
    const events = getEventsByDateBase(dateStr);
    const cycleDay = getCycleDayForDate(dateStr);
    
    calendar.push({
      day: day,
      date: dateStr,
      events: events,
      cycleDay: cycleDay,
      isHoliday: events.some(event => event.category === 'holiday'),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isToday: dateStr === new Date().toISOString().split('T')[0]
    });
  }
  
  return calendar;
};

// Export utility functions for external use
export const formatEventDate = (date, includeDay = true) => {
  const dateObj = new Date(date);
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  
  if (includeDay) {
    const dayNames = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
    const dayName = dayNames[dateObj.getDay()];
    return `${year}年${month}月${day}日 (${dayName})`;
  }
  
  return `${year}年${month}月${day}日`;
};

export const getEventCategoryInfo = (categoryId) => {
  return eventCategories[categoryId] || {
    id: categoryId,
    name: categoryId,
    nameEn: categoryId,
    color: '#6b7280',
    icon: '📅'
  };
};

export const getEventTypeInfo = (typeId) => {
  return eventTypes[typeId] || {
    id: typeId,
    name: typeId,
    nameEn: typeId
  };
}; 